<template>
  <div
    ref="eventEl"
    class="planner-event"
    :class="{
      [eventColorStyle]: true,
      [eventStatus]: true,
      isPassive,
      task: event.task,
      isPast: isPastEvent,
      isTicked: isTaskCompleted,
      isSelected,
      isAllDay,
    }"
    @dblclick="onDblClick($event)"
    @contextmenu.prevent.stop="showContextMenu"
  >
    <BaseCheckbox
      v-if="event.task"
      :is-checked="isTaskCompleted"
      :is-disabled="isBrokenIntegration"
      :checkbox-tooltip="taskCheckboxTooltip(isTaskCompleted, false)"
      checkbox-size="xs"
      @click="onCheckboxClick"
    />
    <div class="title">
      {{ titleDisplay }}
    </div>
    <div v-if="startTime" class="time">
      {{ startTime }}
    </div>
  </div>
</template>

<script lang="ts">
import { addSeconds, isSameDay } from "date-fns";
import { find } from "lodash-es";
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  PropType,
  ref,
  toRefs,
} from "vue";

import type {
  Attendee,
  EventId,
  FullEvent,
  FullTask,
  TaskId,
} from "@ctrl/controller-rpc";

import BaseCheckbox from "@/components/base/BaseCheckbox.vue";
import { useEventInfo } from "@/components/eventInfo";
import {
  colorClassFromSources,
  fetchColorSourceCell,
} from "@/components/graph/calendarColorSource";
import { databaseForTable } from "@/components/graph/predicates";
import * as inject from "@/components/inject";
import { EVENTS_TABLE_ID, TASKS_TABLE_ID } from "@/components/tables";
import { taskCheckboxTooltip } from "@/components/task/checkbox-tooltip";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { useCalendarColorSources } from "@/composables/useCalendarColorSources";
import { bindAsync, useReleaser } from "@/ref";

import { getDaytime } from "../dates";

export default defineComponent({
  components: {
    BaseCheckbox,
  },
  props: {
    end: { type: Date, required: true },
    event: { type: Object as PropType<FullEvent>, required: true },
    isRightClickAllowed: { type: Boolean, required: true },
    isSelected: { type: Boolean, required: true },
    start: { type: Date, required: true },
    task: { type: Object as PropType<FullTask>, required: false },
  },
  emits: {
    open: (
      _v: { kind: "task" | "event"; id: TaskId | EventId },
      _mouseEvent?: MouseEvent
    ) => true,
  },
  async setup(props, { emit }) {
    // HACK: don't use event start or end properties
    const { event, task, start, end, isSelected, isRightClickAllowed } =
      toRefs(props);
    const locale = inject.locale();
    const now = inject.clock();
    const controller = inject.controller();
    const calendars = inject.calendars();
    const contextMenu = inject.contextMenu();
    onBeforeUnmount(contextMenu.value.hide);
    const theme = inject.theme();
    const types = inject.types();
    const profile = inject.profile();
    const releaser = useReleaser();
    const colorSources = releaser.add(useCalendarColorSources(profile));
    const browserStore = useBrowserStore(profile.value.user.accountId);
    const workspaceId = computed(() => profile.value.user.personal.workspaceId);
    const eventEl = ref<HTMLElement>();
    const isDraftEvent = computed(() => event.value.draft);
    const isAllDay = computed(
      () => event.value.allDay || !isSameDay(event.value.start, event.value.end)
    );
    const isPastEvent = computed<boolean>(
      () =>
        // HACK: FullCalendar returns event.end as 03/01 00:00:00
        // to let us know that the event includes all the 02/01
        // then removing 1 second gives 02/01 23:59:59
        addSeconds(end.value, -1) <= now.value
    );
    // Color-source cells are fetched with a dedicated query (not read off
    // the extension row): a source may live several relation hops away, and
    // a row fetch only embeds related rows shallowly.
    const eventColorCellPromise = releaser.add(
      bindAsync(
        computed(() => ({
          rowId: event.value.extension_row,
          source: colorSources.events.value,
          types: types.value,
        })),
        ({ rowId, source, types: typesValue }) =>
          fetchColorSourceCell(
            controller,
            workspaceId.value,
            EVENTS_TABLE_ID,
            rowId,
            {
              source,
              types: typesValue,
              database: databaseForTable(typesValue, EVENTS_TABLE_ID),
            }
          )
      )
    );
    const taskColorCellPromise = releaser.add(
      bindAsync(
        computed(() => ({
          rowId: task.value?.extension_row,
          source: colorSources.tasks.value,
          types: types.value,
        })),
        ({ rowId, source, types: typesValue }) =>
          fetchColorSourceCell(
            controller,
            workspaceId.value,
            TASKS_TABLE_ID,
            rowId,
            {
              source,
              types: typesValue,
              database: databaseForTable(typesValue, TASKS_TABLE_ID),
            }
          )
      )
    );
    const eventInfo = useEventInfo(controller, calendars, event);
    onBeforeUnmount(() =>
      eventInfo.then((eventInfo) => eventInfo.releaseEventInfo())
    );
    const [
      {
        calendar,
        calendarOwnerEmail,
        isPassive,
        isMeetingWithMe,
        isMeetingAccepted,
      },
      eventColorCell,
      taskColorCell,
    ] = await Promise.all([
      eventInfo,
      eventColorCellPromise,
      taskColorCellPromise,
    ]);
    const colorSourceArgs = computed(() => ({
      isAllocation: task.value != null,
      types: types.value,
      sources: {
        events: colorSources.events.value,
        tasks: colorSources.tasks.value,
      },
      eventCell: eventColorCell.value,
      taskCell: taskColorCell.value,
      store: browserStore,
      calendar: calendar.value,
    }));
    const eventColorStyle = computed(() =>
      colorClassFromSources({
        ...colorSourceArgs.value,
        theme: theme.value,
      })
    );
    const titleDisplay = computed(() => event.value.title || "Untitled");
    const startTime = computed<string | null>(() =>
      isAllDay.value
        ? null
        : locale.value.formatDaytime(getDaytime(start.value)).toLowerCase()
    );
    const eventStatus = computed(() => {
      if (!isMeetingWithMe.value) return "default";
      if (event.value.willAttend == null) return "noAnswer";
      if (
        !find(event.value.attendees, (attendee) => {
          return attendee.status !== "no";
        })
      )
        return "noOne";
      if (
        !find(event.value.attendees, (attendee: Attendee) => {
          return (
            attendee.status !== "no" &&
            calendarOwnerEmail.value &&
            !(attendee.email == calendarOwnerEmail.value)
          );
        })
      )
        return "solo";
      return event.value.willAttend;
    });
    const isTaskCompleted = computed<boolean>(() => !!task.value?.completed);
    const onCheckboxClick = () => {
      if (!task.value) return;
      controller.task.update(
        task.value.id,
        {
          completed: isTaskCompleted.value ? null : new Date(),
        },
        "app",
        null
      );
    };
    const onDblClick = (mouseEvent: MouseEvent) => {
      if (isDraftEvent.value) return;
      emit(
        "open",
        task.value
          ? { kind: "task", id: task.value.id }
          : { kind: "event", id: event.value.id },
        mouseEvent
      );
    };
    const showContextMenu = (e: MouseEvent) => {
      if (!isRightClickAllowed.value) return;
      if (!isSelected.value) eventEl.value?.click();
      if (task.value) {
        contextMenu.value.show(
          { type: "allocation", value: { ...task.value, event: event.value } },
          e
        );
      } else {
        contextMenu.value.show(
          {
            type: "event",
            value: {
              ...event.value,
              isCancellable: isMeetingAccepted.value,
              isDeletable: !!calendar.value?.isWritable,
            },
          },
          e
        );
      }
    };
    const isBrokenIntegration = computed(
      () => task.value?.integrationStatus?.status === "broken"
    );
    return {
      eventColorStyle,
      eventEl,
      eventStatus,
      isAllDay,
      isBrokenIntegration,
      isPassive,
      isPastEvent,
      isTaskCompleted,
      onCheckboxClick,
      onDblClick,
      showContextMenu,
      startTime,
      taskCheckboxTooltip,
      titleDisplay,
    };
  },
});
</script>
<style lang="scss" scoped>
@use "sass:color";
.planner-event {
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: row;
  box-sizing: content-box;
  justify-items: center;
  align-items: center;
  column-gap: 6px;
  z-index: $z-index-content-high;
  overflow: hidden;
  padding: 1px 6px;

  &:not(.isSelected).isPast {
    opacity: 0.6;
  }

  .title {
    flex: 1;
    overflow: hidden;
    white-space: nowrap;
    @extend %font-small-medium;
  }

  .time {
    @extend %font-small-medium;

    @include themed() {
      color: t($grey-40);
    }
  }

  &:not(.isSelected) {
    &.calendar-a--light,
    &.calendar-a--dark {
      color: $calendar-a-50;
    }

    &.calendar-b--light,
    &.calendar-b--dark {
      color: $calendar-b-50;
    }

    &.calendar-c--light,
    &.calendar-c--dark {
      color: $calendar-c-50;
    }

    &.calendar-d--light,
    &.calendar-d--dark {
      color: $calendar-d-50;
    }

    &.calendar-e--light,
    &.calendar-e--dark {
      color: $calendar-e-50;
    }

    &.calendar-f--light,
    &.calendar-f--dark {
      color: $calendar-f-50;
    }

    &.calendar-g--light,
    &.calendar-g--dark {
      color: $calendar-g-50;
    }

    &.calendar-h--light,
    &.calendar-h--dark {
      color: $calendar-h-50;
    }

    &.calendar-i--light,
    &.calendar-i--dark {
      color: $calendar-i-50;
    }
  }

  &.isAllDay {
    border-radius: 5px;

    .title,
    .time {
      color: white;
    }

    &.calendar-a--light {
      background-color: $calendar-a-20;

      .title,
      .time {
        color: $calendar-a-80;
      }
    }

    &.calendar-a--dark {
      background-color: $calendar-a-90;

      .title,
      .time {
        @include themed() {
          color: t($primary);
        }
      }
    }

    &.calendar-b--light {
      background-color: $calendar-b-20;

      .title,
      .time {
        color: $calendar-b-80;
      }
    }

    &.calendar-b--dark {
      background-color: $calendar-b-90;

      .title,
      .time {
        @include themed() {
          color: t($primary);
        }
      }
    }

    &.calendar-c--light {
      background-color: $calendar-c-20;

      .title,
      .time {
        color: $calendar-c-80;
      }
    }

    &.calendar-c--dark {
      background-color: $calendar-c-90;

      .title,
      .time {
        @include themed() {
          color: t($primary);
        }
      }
    }

    &.calendar-d--light {
      background-color: $calendar-d-20;

      .title,
      .time {
        color: $calendar-d-80;
      }
    }

    &.calendar-d--dark {
      background-color: $calendar-d-90;

      .title,
      .time {
        @include themed() {
          color: t($primary);
        }
      }
    }

    &.calendar-e--light {
      background-color: $calendar-e-20;

      .title,
      .time {
        color: $calendar-e-80;
      }
    }

    &.calendar-e--dark {
      background-color: $calendar-e-90;

      .title,
      .time {
        @include themed() {
          color: t($primary);
        }
      }
    }

    &.calendar-f--light {
      background-color: $calendar-f-20;

      .title,
      .time {
        color: $calendar-f-80;
      }
    }

    &.calendar-f--dark {
      background-color: $calendar-f-90;

      .title,
      .time {
        @include themed() {
          color: t($primary);
        }
      }
    }

    &.calendar-g--light {
      background-color: $calendar-g-20;

      .title,
      .time {
        color: $calendar-g-80;
      }
    }

    &.calendar-g--dark {
      background-color: $calendar-g-90;

      .title,
      .time {
        @include themed() {
          color: t($primary);
        }
      }
    }

    &.calendar-h--light {
      background-color: $calendar-h-20;

      .title,
      .time {
        color: $calendar-h-80;
      }
    }

    &.calendar-h--dark {
      background-color: $calendar-h-90;

      .title,
      .time {
        @include themed() {
          color: t($primary);
        }
      }
    }

    &.calendar-i--light {
      background-color: $calendar-i-20;

      .title,
      .time {
        color: $calendar-i-80;
      }
    }

    &.calendar-i--dark {
      background-color: $calendar-i-90;

      .title,
      .time {
        @include themed() {
          color: t($primary);
        }
      }
    }
  }

  &.isSelected {
    border-radius: 5px;

    &.calendar-a--light,
    &.calendar-a--dark {
      background-color: $calendar-a-50;

      .title,
      .time {
        color: white;
      }
    }

    &.calendar-b--light,
    &.calendar-b--dark {
      background-color: $calendar-b-50;

      .title,
      .time {
        color: white;
      }
    }

    &.calendar-c--light,
    &.calendar-c--dark {
      background-color: $calendar-c-50;

      .title,
      .time {
        color: white;
      }
    }

    &.calendar-d--light,
    &.calendar-d--dark {
      background-color: $calendar-d-50;

      .title,
      .time {
        color: white;
      }
    }

    &.calendar-e--light,
    &.calendar-e--dark {
      background-color: $calendar-e-50;

      .title,
      .time {
        color: white;
      }
    }

    &.calendar-f--light,
    &.calendar-f--dark {
      background-color: $calendar-f-50;

      .title,
      .time {
        color: white;
      }
    }

    &.calendar-g--light,
    &.calendar-g--dark {
      background-color: $calendar-g-50;

      .title,
      .time {
        color: white;
      }
    }

    &.calendar-h--light,
    &.calendar-h--dark {
      background-color: $calendar-h-50;

      .title,
      .time {
        color: white;
      }
    }

    &.calendar-i--light,
    &.calendar-i--dark {
      background-color: $calendar-i-50;

      .title,
      .time {
        color: white;
      }
    }
  }

  &:not(.isSelected):hover {
    border-radius: 5px;

    &.calendar-a--light {
      background-color: $calendar-a-20;
    }

    &.calendar-a--dark {
      background-color: $calendar-a-90;
    }

    &.calendar-b--light {
      background-color: $calendar-b-20;
    }

    &.calendar-b--dark {
      background-color: $calendar-b-90;
    }

    &.calendar-c--light {
      background-color: $calendar-c-20;
    }

    &.calendar-c--dark {
      background-color: $calendar-c-90;
    }

    &.calendar-d--light {
      background-color: $calendar-d-20;
    }

    &.calendar-d--dark {
      background-color: $calendar-d-90;
    }

    &.calendar-e--light {
      background-color: $calendar-e-20;
    }

    &.calendar-e--dark {
      background-color: $calendar-e-90;
    }

    &.calendar-f--light {
      background-color: $calendar-f-20;
    }

    &.calendar-f--dark {
      background-color: $calendar-f-90;
    }

    &.calendar-g--light {
      background-color: $calendar-g-20;
    }

    &.calendar-g--dark {
      background-color: $calendar-g-90;
    }

    &.calendar-h--light {
      background-color: $calendar-h-20;
    }

    &.calendar-h--dark {
      background-color: $calendar-h-90;
    }

    &.calendar-i--light {
      background-color: $calendar-i-20;
    }

    &.calendar-i--dark {
      background-color: $calendar-i-90;
    }
  }

  &:not(.isSelected).isAllDay:hover {
    border-radius: 5px;

    &.calendar-a--light {
      background-color: color.adjust($calendar-a-20, $lightness: -3%);
    }

    &.calendar-a--dark {
      background-color: color.adjust($calendar-a-90, $lightness: -5%);
    }

    &.calendar-b--light {
      background-color: color.adjust($calendar-b-20, $lightness: -3%);
    }

    &.calendar-b--dark {
      background-color: color.adjust($calendar-b-90, $lightness: -5%);
    }

    &.calendar-c--light {
      background-color: color.adjust($calendar-c-20, $lightness: -3%);
    }

    &.calendar-c--dark {
      background-color: color.adjust($calendar-c-90, $lightness: -5%);
    }

    &.calendar-d--light {
      background-color: color.adjust($calendar-d-20, $lightness: -3%);
    }

    &.calendar-d--dark {
      background-color: color.adjust($calendar-d-90, $lightness: -5%);
    }

    &.calendar-e--light {
      background-color: color.adjust($calendar-e-20, $lightness: -3%);
    }

    &.calendar-e--dark {
      background-color: color.adjust($calendar-e-90, $lightness: -5%);
    }

    &.calendar-f--light {
      background-color: color.adjust($calendar-f-20, $lightness: -3%);
    }

    &.calendar-f--dark {
      background-color: color.adjust($calendar-f-90, $lightness: -5%);
    }

    &.calendar-g--light {
      background-color: color.adjust($calendar-g-20, $lightness: -3%);
    }

    &.calendar-g--dark {
      background-color: color.adjust($calendar-g-90, $lightness: -5%);
    }

    &.calendar-h--light {
      background-color: color.adjust($calendar-h-20, $lightness: -3%);
    }

    &.calendar-h--dark {
      background-color: color.adjust($calendar-h-90, $lightness: -5%);
    }

    &.calendar-i--light {
      background-color: color.adjust($calendar-i-20, $lightness: -3%);
    }

    &.calendar-i--dark {
      background-color: color.adjust($calendar-i-90, $lightness: -5%);
    }
  }
}
</style>
